import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, HostListener, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { trigger, transition, style, animate } from '@angular/animations';
import { interval, merge, startWith, switchMap } from 'rxjs';
import { linksSettings } from '../../interfaces/linksSettings';
import { UsuarioService } from '../../../Cadastro/usuario/services/Usuario.service';
import { Usuario } from '../../../Cadastro/usuario/model/Usuario.model';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../../../auth/auth.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PermissionsService } from '../../../../shared/services/config/permissions.service';
import { EmpresaDropdownComponent } from '../empresa-dropdown/empresa-dropdown.component';
import { NotificacaoBarService } from '../../../Feed/services/notifcacaoBar.service';
import { UserService } from '../../../../shared/services/config/user.service';
import { NotificacaoUsuarioSistemaService } from '../../../Feed/services/notificacaoUsuario.service';
import { NotificacaoUsuarioSistema } from '../../../Feed/model/NotificacaoUsuarioSistema.model';
import { NovidadeSistema } from '../../../Feed/model/NovidadeSistema.model';
import { NovidadeSistemaService } from '../../../Feed/services/novidadeSistema.service';
import { ToastService } from '../../../../shared/components/toast/services/toast.service';

const NOTIFICATION_POLLING_INTERVAL_MS = 20_000;

@Component({
  standalone: true,
  selector: 'app-user-profile-header',
  imports: [CommonModule, RouterModule, TranslateModule, EmpresaDropdownComponent],
  templateUrl: './user-profile-header.component.html',
  styleUrl: './user-profile-header.component.scss',
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('fadeBox', [
      transition(':enter', [
        style({ opacity: 0, height: '0px' }),
        animate('200ms ease', style({ opacity: 1, height: '230px' })),
      ]),
      transition(':leave', [
        animate('200ms ease', style({ opacity: 0, height: '0px' })),
      ]),
    ]),
  ],
})
export class UserProfileHeaderComponent implements OnInit {
  private readonly _service = inject(UsuarioService);
  private readonly _userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly authService = inject(AuthService);
  private readonly translate = inject(TranslateService);
  private readonly userPermissionService = inject(PermissionsService);
  private readonly notificacaoBarService = inject(NotificacaoBarService);
  private readonly notificacaoUsuarioService = inject(NotificacaoUsuarioSistemaService);
  private readonly _novidadeSistemaService = inject(NovidadeSistemaService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);

  private _isFirstPoll = true;
  private _quantidadeAnterior = 0;

  model!: Usuario;
  userIsAdmin: boolean = false;
  exibeEmpresa: boolean = false;
  openSettings: boolean = false;
  linksSettings: Array<linksSettings> = [
    {
      link: "profile",
      icon: "account_circle",
      text: "COMMON.PROFILE"
    },
    {
      link: "configuracoes-sistema",
      icon: "settings",
      text: "COMMON.SYSTEM_PREFERENCES"
    }
  ];
  listaNotificacoes$ : NotificacaoUsuarioSistema[] = [];
  quantidadeNotificacoesNaoLidas: number = 0;
  listaDeNovidadesDoSistema: NovidadeSistema[] = [];

  ngOnInit(): void {
    this.load();
    this.iniciarPollingNotificacoes();

    this.notificacaoBarService.mudancaNotificacao$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.atualizarContador());
  }

  load() {
    this._service.getPerfil().subscribe(data => {
      this.model = data;
      this.cdr.detectChanges();

      this.userPermissionService.isAdmin().then(isAdmin => {
        this.userIsAdmin = isAdmin;
        var usuarioEmpresaLogado = this._userService.getUsuarioEmpresaLogado();
        if (usuarioEmpresaLogado.length > 1 || this.userIsAdmin) {
          this.exibeEmpresa = true;
          this.cdr.detectChanges();
        }
      });
    });
  }

  private iniciarPollingNotificacoes(): void {
    merge(
    interval(NOTIFICATION_POLLING_INTERVAL_MS),
    this.notificacaoBarService.mudancaNotificacao$
    ).pipe(
      startWith(0),
      switchMap(() => this.notificacaoUsuarioService.GetNotificacaoUsuario()),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (data) => {
        this.listaNotificacoes$ = data;
        const novasNaoLidas = this.listaNotificacoes$.filter(n => !n.flagVisualizado).length;

        if (!this._isFirstPoll && novasNaoLidas > this._quantidadeAnterior) {
          const diff = novasNaoLidas - this._quantidadeAnterior;
          const msg = diff === 1
            ? this.translate.instant('TOAST.NEW_NOTIFICATION')
            : this.translate.instant('TOAST.NEW_NOTIFICATIONS', { diff });
          this.toastService.show(msg, 'info');
        }

        this._quantidadeAnterior = novasNaoLidas;
        this._isFirstPoll = false;
        this.quantidadeNotificacoesNaoLidas = novasNaoLidas;

        this._novidadeSistemaService.get().subscribe({
          next: (novidades) => {
            this.listaDeNovidadesDoSistema = novidades.filter(novidade =>
              this.listaNotificacoes$.some(nt => nt.novidadeSistemaId === novidade.id && !nt.flagVisualizado)
            );
            this.cdr.detectChanges();
          },
          error: (err) => console.error('Erro ao carregar as novidades do sistema:', err)
        });

        this.cdr.detectChanges();
      },
      error: (err) => console.error('Erro ao carregar as notificações do usuário:', err)
    });
  }

  get avatarUrl(): string {
    if(this.model != undefined && this.model.avatar != null && this.model.avatar != '') {
      return this.model.avatar;
    }
    return  '/assets/icons/user.png';
  }

  openLinkSettings(link: string) {
    this.router.navigate([link]);
    this.openSettings = false;
  }

  exit() {
    this.authService.logout();
  }

  openClickSettings() {
    this.openSettings = !this.openSettings;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const target = event.target as HTMLElement;
    // aceita cliques no botão de abertura OU no box-settings-profile
    if (!target.closest('.box-settings-profile') &&
      !target.closest('.open-settings-trigger')) {
      this.openSettings = false;
    }
  }

  openNotificacaoBar() {
    this.notificacaoBarService.abrir(this.listaDeNovidadesDoSistema, this.listaNotificacoes$);
  }

  atualizarContador() {
    this.notificacaoUsuarioService.GetNotificacaoUsuario().subscribe({
      next: (data) => {
        this.listaNotificacoes$ = data;
        this.quantidadeNotificacoesNaoLidas = this.listaNotificacoes$.filter(n => !n.flagVisualizado).length;

        this._novidadeSistemaService.get().subscribe({
          next: (novidades) => {
            this.listaDeNovidadesDoSistema = novidades.filter(novidade => {
              return this.listaNotificacoes$.some(nt => nt.novidadeSistemaId === novidade.id && !nt.flagVisualizado);
            });
            this.cdr.detectChanges();
          }
        });

        this.cdr.detectChanges();
      }
    });
  }

}
